/**
 * EmployerOnboardingPage.js — RC 1.62 Consents-Only Posture.
 *
 * Doctrine (frozen — see RC_1_62_ROLE_AUTHORITY_MATRIX.md §5):
 *   - Public organisation creation is OFF.
 *     `PUBLIC_ORG_CREATION_ENABLED=false` (backend).
 *   - Admin/founding-access provisions organisations.
 *   - Invite links attach users to organisations.
 *   - Onboarding does NOT create an organisation.
 *   - There is no "activation pending" lifecycle.
 *   - There is no outbound transactional email anywhere on the platform.
 *
 * This page therefore collects ONLY the legally-required consents
 * (privacy policy + employer terms) on first employer-side sign-in,
 * flips `hasProfile=true` so route resolution stops bouncing back
 * here, and lands the user on `/employer`. From there they either
 * see their workspace (if they followed an invite link / admin
 * attached them to an org) or the `EmployerNoMembershipStub` (calm,
 * terminal — no org-creation affordances).
 *
 * Out of scope (and explicitly NOT brought back from the legacy
 * three-step wizard):
 *   - Organisation basics (name/description/sector/size_band/website)
 *   - Culture & preferences (working_model/location_mode/why_join)
 *   - Any `POST /api/employer/org` call (the backend 403s it anyway
 *     while PUBLIC_ORG_CREATION_ENABLED is false; we do not even
 *     attempt the call so we cannot leak partial wizard state).
 *
 * No new endpoints. No schema changes. No new RPCs. No new RLS.
 * Uses existing `public.consents` table (RLS owns insert authority).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Card } from '@/components/ui/card';
import {
  Loader2, Check, AlertCircle, ShieldCheck, ScrollText,
} from 'lucide-react';
import { toast } from 'sonner';
import BrandLogo from '@/components/BrandLogo';


/* ── Required consents (unchanged from legacy form) ───────────── */

const EMPLOYER_CONSENTS = [
  {
    key: 'privacy_policy',
    label: 'Privacy Policy',
    description:
      'I have read and agree to the NDORFlow Privacy Policy.',
  },
  {
    key: 'employer_terms',
    label: 'Employer Terms',
    description:
      'I agree to the NDORFlow Employer Terms — including the PII-boundary rules, evidence-only screening, and candidate-controlled signal delivery.',
  },
];


export default function EmployerOnboardingPage() {
  const { user, hasProfile, refreshProfile } = useAuth();
  const navigate = useNavigate();

  const [consents, setConsents]   = useState({});
  const [saving, setSaving]       = useState(false);
  const [completed, setCompleted] = useState(false);
  const [saveError, setSaveError] = useState('');

  // If consents are already recorded (hasProfile flipped), this page
  // has no remaining purpose — bounce to /employer immediately.
  useEffect(() => {
    if (hasProfile) navigate('/employer', { replace: true });
  }, [hasProfile, navigate]);

  // Short post-success delay then redirect.
  useEffect(() => {
    if (!completed) return undefined;
    const tid = setTimeout(
      () => navigate('/employer', { replace: true }),
      1400,
    );
    return () => clearTimeout(tid);
  }, [completed, navigate]);

  const allRequiredConsents = EMPLOYER_CONSENTS.every((c) => !!consents[c.key]);
  const canFinish = allRequiredConsents && !saving && !!user?.id;

  /* ── consents persistence (unchanged from legacy form) ──────── */
  async function saveConsentsRows(userId) {
    const rows = EMPLOYER_CONSENTS
      .filter((c) => !!consents[c.key])
      .map((c) => ({
        user_id: userId,
        consent_type: c.key,
        accepted: true,
        policy_version: '1.0',
        accepted_at: new Date().toISOString(),
      }));
    if (rows.length === 0) return { ok: false, msg: 'No consents accepted.' };
    const { data, error } = await supabase
      .from('consents')
      .insert(rows)
      .select();
    if (error) return { ok: false, msg: `Consent save failed: ${error.message}` };
    if (!data || data.length === 0) {
      return { ok: false, msg: 'Consent save blocked by RLS on public.consents.' };
    }
    return { ok: true };
  }

  async function handleFinish() {
    setSaveError('');
    if (!canFinish) return;
    setSaving(true);
    try {
      const consentResult = await saveConsentsRows(user.id);
      if (!consentResult.ok) {
        setSaveError(consentResult.msg);
        toast.error(consentResult.msg);
        return;
      }
      // Refreshing the profile flips `hasProfile=true` so the
      // protected-route bounce-back stops firing.
      await refreshProfile();
      setCompleted(true);
    } catch (err) {
      const msg = err?.message || 'Unexpected error.';
      setSaveError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  /* ── completed state ────────────────────────────────────────── */
  if (completed) {
    return (
      <div
        className="min-h-screen bg-ndor-canvas flex items-center justify-center px-4"
        data-testid="employer-onboarding-complete"
      >
        <div className="text-center max-w-sm">
          <div className="mx-auto w-14 h-14 rounded-full bg-ndor-success-light flex items-center justify-center mb-5">
            <Check className="h-7 w-7 text-ndor-success" />
          </div>
          <h1 className="text-2xl font-semibold text-ndor-ink font-outfit">
            Consents recorded
          </h1>
          <p className="text-sm text-ndor-ink-secondary mt-2 font-manrope">
            Taking you to your employer workspace…
          </p>
        </div>
      </div>
    );
  }

  /* ── shell ──────────────────────────────────────────────────── */
  return (
    <div
      className="min-h-screen bg-ndor-canvas flex flex-col"
      data-testid="employer-onboarding-page"
    >
      <header className="border-b border-ndor-edge bg-ndor-surface px-4 py-4 sm:px-6">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <BrandLogo variant="symbol" size={32} />
          <span className="text-lg font-serif-display font-semibold tracking-tight text-ndor-ink">
            NDORFlow
          </span>
          <span className="ml-auto text-xs text-ndor-ink-muted font-manrope uppercase tracking-wider">
            Employer access
          </span>
        </div>
      </header>

      <main className="flex-1 px-4 py-10 sm:px-6">
        <div className="max-w-2xl mx-auto space-y-6">
          {saveError && (
            <div
              className="rounded-lg bg-ndor-error-light border border-ndor-error-border p-4 flex items-start gap-3"
              data-testid="employer-onboarding-error"
            >
              <AlertCircle className="h-5 w-5 text-ndor-error shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-ndor-error">Consent save failed</p>
                <p className="text-sm text-ndor-ink-secondary mt-1">{saveError}</p>
              </div>
            </div>
          )}

          <section data-testid="employer-onboarding-step-consents-body" className="space-y-6">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-ndor-ink font-outfit flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-ndor-ink-muted" />
                Review &amp; consents
              </h1>
              <p className="text-sm text-ndor-ink-secondary mt-1 font-manrope">
                Confirm the platform consents below to continue to
                your employer workspace.
              </p>
            </div>

            <Card
              className="border-ndor-edge bg-ndor-surface px-4 py-3"
              data-testid="employer-onboarding-doctrine-note"
            >
              <div className="flex items-start gap-2 text-xs text-ndor-ink-muted leading-relaxed">
                <ScrollText className="h-3.5 w-3.5 mt-0.5 shrink-0 text-ndor-gold" />
                <span>
                  NDORFlow organisations are admin-provisioned.
                  Creating an account does not create an organisation;
                  workspaces are attached to your account through a
                  valid invitation link or directly by a platform
                  administrator. If you reached this page without an
                  invitation, complete your consents and you will land
                  on a holding page until your access is provisioned.
                </span>
              </div>
            </Card>

            <div className="space-y-4">
              <p className="text-sm font-medium text-ndor-ink">Consents</p>
              {EMPLOYER_CONSENTS.map((c) => (
                <label
                  key={c.key}
                  className="flex items-start gap-3 rounded-lg border border-ndor-edge p-4 cursor-pointer hover:bg-ndor-surface-alt/50 transition-colors"
                >
                  <Checkbox
                    checked={!!consents[c.key]}
                    onCheckedChange={() =>
                      setConsents((s) => ({ ...s, [c.key]: !s[c.key] }))
                    }
                    className="mt-0.5"
                    data-testid={`employer-consent-${c.key}`}
                  />
                  <div>
                    <p className="text-sm font-medium text-ndor-ink">
                      {c.label}
                      <span className="text-ndor-error ml-1">*</span>
                    </p>
                    <p className="text-xs text-ndor-ink-muted mt-0.5 leading-relaxed">
                      {c.description}
                    </p>
                  </div>
                </label>
              ))}
              <p className="text-[11px] text-ndor-ink-muted">
                * Required. Version 1.0 — timestamped at submission.
              </p>
            </div>
          </section>
        </div>
      </main>

      <footer className="border-t border-ndor-edge bg-ndor-surface px-4 py-4 sm:px-6">
        <div className="max-w-2xl mx-auto flex items-center justify-end">
          <Button
            onClick={handleFinish}
            disabled={!canFinish}
            data-testid="employer-onboarding-finish"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
            {saving ? 'Saving…' : 'Continue to workspace'}
          </Button>
        </div>
      </footer>
    </div>
  );
}
